/**
 * Global "AI Jobs" tray context — replicates the forked-cambridge-online
 * pattern. One context, three kinds: "split" | "quiz" | "video". Adding a
 * new kind is a single entry in KIND_CONFIG below.
 *
 * Persists active job IDs in localStorage (cco_ai_jobs_v2) so a refresh
 * doesn't lose visibility. Polls every 2 seconds. Per-job onDone callback
 * fires exactly once.
 */
import React, {
    createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const STORAGE_KEY = "cco_ai_jobs_v2";
const POLL_INTERVAL_MS = 2000;

const KIND_CONFIG = {
    split: {
        label: "PDF → lessons",
        successMessage: (j) =>
            `Chapter split complete — ${j.lessons_inserted || j.target_count} lessons created in “${j.chapterTitle || "chapter"}”`,
        statusUrl: (j) =>
            `/chapters/${j.chapterId}/lessons/ai-split-pdf/status?job_id=${j.jobId}`,
        // returns 0..100
        computePercent: (s) => {
            if (!s) return 0;
            if (s.stage === "done") return 100;
            if (s.stage === "queued") return 2;
            const cur = s.current_batch || 0;
            const tot = s.total_batches || 1;
            // extracting=10%, then each batch = 90%/total
            const base = s.stage === "extracting" ? 10 : 10 + Math.floor((cur / tot) * 88);
            return Math.min(98, base);
        },
        stageLabel: (s) => {
            if (!s) return "queued";
            if (s.stage === "queued") return "Queued";
            if (s.stage === "extracting") return "Reading PDF";
            if (s.stage === "ai-drafting") return `Drafting lessons (batch ${s.current_batch}/${s.total_batches})`;
            if (s.stage === "saving") return "Saving lessons";
            if (s.stage === "done") return "Done";
            if (s.stage === "error") return s.error || "Failed";
            return s.stage;
        },
        isDone: (s) => s?.stage === "done",
        isError: (s) => s?.stage === "error",
        eventName: "cco:chapter-lessons-changed",
        icon: "ScrollText",
    },
    quiz: {
        label: "Auto-quiz",
        successMessage: (j) => `Quiz generated for “${j.chapterTitle || "chapter"}”`,
        statusUrl: (j) =>
            `/chapters/${j.chapterId}/quiz/auto-generate/status?job_id=${j.jobId}`,
        computePercent: (s) => {
            if (!s) return 0;
            if (s.status === "done" || s.stage === "done") return 100;
            const total = s.total || 1;
            const progress = s.progress || 0;
            return Math.min(98, Math.max(2, Math.floor((progress / total) * 100)));
        },
        stageLabel: (s) => {
            if (!s) return "queued";
            if (s.status === "done") return "Done";
            if (s.status === "error" || s.stage === "error") return s.error || "Failed";
            const stage = s.stage || s.status;
            return `${stage} (${s.progress || 0}/${s.total || 0})`;
        },
        isDone: (s) => s?.status === "done" || s?.stage === "done",
        isError: (s) => s?.status === "error" || s?.stage === "error",
        eventName: "cco:chapter-quiz-changed",
        icon: "Brain",
    },
    "lesson-quiz": {
        label: "Lesson quiz",
        successMessage: (j) => `Lesson quiz generated for “${j.lessonTitle || "lesson"}”`,
        statusUrl: (j) =>
            `/lessons/${j.lessonId}/quiz/auto-generate/status?job_id=${j.jobId}`,
        computePercent: (s) => {
            if (!s) return 0;
            if (s.status === "done" || s.stage === "done") return 100;
            if (s.stage === "drafting questions") return 60;
            if (s.stage === "reading lesson") return 30;
            return 10;
        },
        stageLabel: (s) => {
            if (!s) return "queued";
            if (s.status === "done") return "Done";
            if (s.status === "failed" || s.stage === "failed") return s.error || "Failed";
            return s.stage || s.status;
        },
        isDone: (s) => s?.status === "done" || s?.stage === "done",
        isError: (s) => s?.status === "failed" || s?.stage === "failed",
        eventName: "cco:lesson-quiz-changed",
        icon: "Brain",
    },
    video: {
        label: "Render video",
        successMessage: (j) => `Video rendered for “${j.lessonTitle || "lesson"}”`,
        statusUrl: (j) => `/lesson-video-jobs/${j.jobId}`,
        computePercent: (s) => {
            if (!s) return 0;
            if (s.status === "done") return 100;
            const total = s.total || 1;
            const cur = s.current || 0;
            return Math.min(98, Math.max(2, Math.floor((cur / total) * 100)));
        },
        stageLabel: (s) => {
            if (!s) return "queued";
            if (s.status === "done") return "Done";
            if (s.status === "error") return s.error || "Failed";
            return `${s.step || s.status} (${s.current || 0}/${s.total || 0})`;
        },
        isDone: (s) => s?.status === "done",
        isError: (s) => s?.status === "error",
        eventName: "cco:lesson-video-rendered",
        icon: "Film",
    },
};

const AiJobsContext = createContext({
    jobs: [],
    trackJob: () => {},
    dismissJob: () => {},
    getJobStatus: () => null,
});

export function useAiJobs() { return useContext(AiJobsContext); }

function loadFromStorage() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter((j) => j && j.jobId && j.kind && KIND_CONFIG[j.kind]);
    } catch { return []; }
}

function saveToStorage(jobs) {
    try {
        // Persist only the small "header" — not the status payload which may be large.
        const headers = jobs.map(({ status, onDone, ...rest }) => rest); // eslint-disable-line no-unused-vars
        localStorage.setItem(STORAGE_KEY, JSON.stringify(headers));
    } catch { /* localStorage full / disabled — ignore */ }
}

export function AiJobsProvider({ children }) {
    const [jobs, setJobs] = useState(() => loadFromStorage());
    // Stable refs so the poll loop doesn't restart on every state change
    const jobsRef = useRef(jobs);
    jobsRef.current = jobs;
    const callbacksRef = useRef({}); // jobId -> onDone

    const trackJob = useCallback(({
        kind, jobId, chapterId, chapterTitle, lessonId, lessonTitle,
        total, onDone, meta,
    }) => {
        if (!KIND_CONFIG[kind] || !jobId) {
            return;
        }
        if (onDone) callbacksRef.current[jobId] = onDone;
        setJobs((prev) => {
            if (prev.find((j) => j.jobId === jobId)) return prev;
            const next = [...prev, {
                kind, jobId, chapterId, chapterTitle, lessonId, lessonTitle,
                total: total || 0, startedAt: Date.now(),
                status: null, percent: 0, done: false, error: null,
                meta: meta || {},
            }];
            saveToStorage(next);
            return next;
        });
    }, []);

    const dismissJob = useCallback((jobId) => {
        delete callbacksRef.current[jobId];
        setJobs((prev) => {
            const next = prev.filter((j) => j.jobId !== jobId);
            saveToStorage(next);
            return next;
        });
    }, []);

    const getJobStatus = useCallback((jobId) => {
        const j = jobsRef.current.find((x) => x.jobId === jobId);
        return j || null;
    }, []);

    // Poll loop — one effect, runs continuously
    useEffect(() => {
        let cancelled = false;
        const tick = async () => {
            const snapshot = jobsRef.current;
            const inFlight = snapshot.filter((j) => !j.done && !j.error);
            if (inFlight.length === 0) return;

            const results = await Promise.allSettled(inFlight.map(async (j) => {
                const cfg = KIND_CONFIG[j.kind];
                try {
                    const { data } = await api.get(cfg.statusUrl(j));
                    return { j, status: data };
                } catch (err) {
                    // Distinguish 404 (job gone) from transient network errors
                    if (err?.response?.status === 404) {
                        return { j, status: null, gone: true };
                    }
                    return { j, status: null, transient: true };
                }
            }));
            if (cancelled) return;
            setJobs((prev) => {
                let mutated = false;
                const next = prev.map((j) => {
                    const r = results.find((x) =>
                        x.status === "fulfilled" && x.value?.j?.jobId === j.jobId,
                    );
                    if (!r) return j;
                    const { status, gone } = r.value;
                    if (gone) {
                        mutated = true;
                        return { ...j, error: "Job no longer exists", done: false };
                    }
                    if (!status) return j; // transient
                    const cfg = KIND_CONFIG[j.kind];
                    const isDone = cfg.isDone(status);
                    const isError = cfg.isError(status);
                    const percent = cfg.computePercent(status);
                    if (j.status !== status || j.percent !== percent || j.done !== isDone) {
                        mutated = true;
                    }
                    const updated = { ...j, status, percent, done: isDone, error: isError ? (status.error || "Failed") : null };
                    if (isDone && !j.done) {
                        // Fire success side effects exactly once
                        try { toast.success(cfg.successMessage(updated)); } catch { /* noop */ }
                        try {
                            window.dispatchEvent(new CustomEvent(cfg.eventName, {
                                detail: { jobId: j.jobId, kind: j.kind, chapterId: j.chapterId, lessonId: j.lessonId },
                            }));
                        } catch { /* noop */ }
                        const cb = callbacksRef.current[j.jobId];
                        if (cb) { try { cb(status); } catch { /* noop */ } }
                    } else if (isError && !j.error) {
                        try { toast.error(`${cfg.label}: ${status.error || "Failed"}`); } catch { /* noop */ }
                    }
                    return updated;
                });
                if (mutated) saveToStorage(next);
                return mutated ? next : prev;
            });
        };
        const id = setInterval(tick, POLL_INTERVAL_MS);
        // initial tick immediately
        tick();
        return () => { cancelled = true; clearInterval(id); };
    }, []);

    const value = useMemo(() => ({ jobs, trackJob, dismissJob, getJobStatus }), [jobs, trackJob, dismissJob, getJobStatus]);
    return <AiJobsContext.Provider value={value}>{children}</AiJobsContext.Provider>;
}

export { KIND_CONFIG };
